import type { UpdateTruckInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { recordOdometerReading, updateTruck } from '../trucks.api';
import { useTruckDocuments } from './use-truck-documents';
import { useTruckRecord } from './use-truck-record';

/** A truck's record and odometer: its record (`useTruckRecord`) plus the Details tab's two mutations. Document mutations live in `use-truck-documents.ts`. */
export function useTruckDetail(id: string) {
  const { state, reload } = useTruckRecord(id);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function update(input: UpdateTruckInput): Promise<boolean> {
    setUpdateError(null);
    setSaving(true);
    try {
      await updateTruck(id, input);
      await reload();
      toast.success('Truck updated');
      return true;
    } catch (err) {
      setUpdateError(
        err instanceof ApiError && err.status === 409 ? 'A truck with this registration already exists.' : 'Could not save — check the fields and try again.',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function recordReading(odometerKm: number): Promise<boolean> {
    try {
      await recordOdometerReading(id, odometerKm);
      await reload();
      toast.success(`Odometer reading of ${odometerKm.toLocaleString()} km recorded`);
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError && err.status === 409 ? 'That reading would move the odometer backward' : "Couldn't record the reading");
      return false;
    }
  }

  const documents = useTruckDocuments(id, reload);
  return { state, updateError, saving, update, recordReading, ...documents };
}
